import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { summary, analytics, management, advanced, campaigns, documentation, brandCharts } from './navigationRoutes';
import { brandFeatures, brandManagement } from './navigationRoutes';
import UserDropdown from '../../components/userDropdown';
import { getSelectedProject } from '../../redux/projects/actions';
import { getSelectedPortal } from '../../redux/portal/actions';
import icons from '../../assets/icons';

import './styles.scss';

const routes = [summary(), analytics(), campaigns(), management(), advanced(), brandCharts()];
routes.push(brandFeatures());
routes.push(brandManagement());
routes.push(documentation());

const MainNavigation = ({
  location,
  history,
  user,
  templates,
  hideNavigation,
  stopImpersonating,
  openImpersonateModal,
  featureFlags,
  isPortalPreview
}) => {
  const [expanded, setExpanded] = useState('');

  const selectedProject = useSelector(getSelectedProject);
  const selectedPortal = useSelector(getSelectedPortal);

  useEffect(() => {
    setSelectedRoute();
  }, [location]);

  useEffect(() => {
    // checkExpiredLicenses();
  }, [location.pathname, selectedProject]);

  const userOptions = [
    {
      name: 'Profile',
      icon: 'userDark',
      url: '/#/profile'
    },
    ...(!isPortalPreview
      ? [
          {
            name: 'Impersonate User',
            icon: 'impersonateEye',
            action: openImpersonateModal,
            isAdminOnly: true
          }
        ]
      : []),
    {
      name: 'Logout',
      icon: 'logoutDark',
      url: '/#/logout'
    }
  ];

  // const checkExpiredLicenses = async () => {
  //   if (selectedProject) {
  //     API.getSiteGroupStats(selectedProject.id)
  //       .then(response => {
  //         const hasExpired = response.licenseCountByStatus.expired > 0;
  //         if (hasExpired) {
  //           dispatch(
  //             setAlert(
  //               utils.generateAlert(
  //                 'You have expired licenses in your project.',
  //                 'error',
  //                 -1,
  //                 'https://subscriptions.zoho.com/portal/admobilize/login',
  //                 true
  //               )
  //             )
  //           );
  //         }
  //       })
  //       .catch(console.error);
  //   }
  // };

  const setSelectedRoute = () => {
    // Open submenu in case the selected page is a subitem on page load
    const selectedRoute = routes.find(
      route =>
        isRouteVisible(route) &&
        route.subitems &&
        route.subitems.some(item => isRouteVisible(item) && item.to === location.pathname)
    );

    setExpanded(selectedRoute ? selectedRoute.key : '');
  };

  const handleNavClick = (route, event) => {
    if (event) {
      if (route.subitems) {
        // Disable default behaviour for click when a navbar item have subitems
        event.preventDefault();

        // Navigate immediately to the first visible subitem
        const [firstItem] = getVisibleSubitems(route);
        setExpanded(route.key);
        if (firstItem) {
          history.replace(firstItem.to);
        }
      } else {
        history.replace(route.to);
      }
    }
  };

  const handleNavArrowClick = (route, event) => {
    if (event && route.subitems) {
      // Disable default behaviour for click when a navbar item have subitems
      event.preventDefault();

      if (expanded === route.key) {
        setExpanded('');
      } else {
        if (route.subitems) {
          setExpanded(route.key);
        }
      }
    } else {
      if (expanded !== '') {
        setExpanded('');
      }
    }
  };

  const isActive = (location, target) => {
    // Menu entries with subitems should have target (to) URL as #
    if (target === '#') {
      return false;
    }

    if (target === '/documentation' && location.pathname.startsWith(`${target}/`)) {
      return true;
    }

    const homepageTarget = '/summary';
    const homepageOptions = ['/index.html', '/'];

    // The homepage can have URL variations like the ones listed obove
    if (target === homepageTarget && homepageOptions.includes(location.pathname)) {
      return true;
    } else if (location.pathname === target) {
      return true;
    }

    return false;
  };

  const getRouteIcon = route => route.image || icons[route.icon];

  const getActivePortalFeatures = () => {
    const features = selectedPortal?.activeFeatures ?? selectedPortal?.features ?? selectedPortal?.feature;
    return Array.isArray(features) ? features : features ? [features] : [];
  };

  const isRouteVisible = route => {
    const isPowerUser = user && user.admin;
    const hasMobileTemplate = templates?.some(t => t.compatibleDatasources.includes('MOBILE_M'));
    const currentUserType = isPortalPreview ? 'BRAND' : user && user.type;

    if (currentUserType === 'BRAND' && route.userType !== 'BRAND' && !route.isUniversal) return false;
    if (currentUserType !== 'BRAND' && route.userType === 'BRAND') return false;
    if (route.isAdminOnly && !isPowerUser) return false;
    if (route.isMobileOnly && !hasMobileTemplate) return false;
    if (route.featureEnabled && !featureFlags[route.featureEnabled]) return false;
    if (route.showWhenPortalHasFeatures && getActivePortalFeatures().length === 0) return false;
    if (route.requiredPortalFeature && !getActivePortalFeatures().includes(route.requiredPortalFeature)) return false;
    if (route.subitems && !route.showWhenPortalHasFeatures && !route.subitems.some(isRouteVisible)) return false;

    return true;
  };

  const getVisibleSubitems = route => (route.subitems || []).filter(isRouteVisible);

  const renderNavOptions = items => {

    const visibleItems = items.filter(isRouteVisible);

    return (
      <ul className="d-flex flex-column flex-fill">
        {visibleItems.map(route => {
          const isExpanded = expanded === route.key;
          const label = (
            <>
              <img src={getRouteIcon(route)} alt="" />
              <span>{route.name}</span>
            </>
          );
          const itemClassName = [
            isExpanded && 'active-submenu',
            route.stickToBottom && 'bottom-navigation-item'
          ].filter(Boolean).join(' ');

          return (
            <li key={route.key} className={itemClassName}>
              <div className={`menu-entry ${isActive(location, route.to) ? 'active' : ''}`}>
                {route.subitems ? (
                  <button
                    className="menu-entry-label"
                    onClick={event => handleNavClick(route, event)}
                    type="button">
                    {label}
                  </button>
                ) : (
                  <Link className="menu-entry-label" to={route.to}>{label}</Link>
                )}
                {route.subitems && (
                  <div className="submenu-indicator" onClick={event => handleNavArrowClick(route, event)}>
                    <div className={`expand-button ${isExpanded ? 'expanded' : ''}`}>
                      <img src={icons.angleDown} alt="" />
                    </div>
                  </div>
                )}
              </div>
              {isExpanded && route.subitems && renderNavOptions(route.subitems)}
            </li>
          );
        })}
      </ul>
    );
  };

  if (!user || hideNavigation) {
    return <div />;
  }

  return (
    <div className="sidebar-wrapper">
      <div className="sidebar-navigation">
        <a href="/" className="company-logo">
          {''}
        </a>
        {renderNavOptions(routes)}
        <UserDropdown stopImpersonating={stopImpersonating} user={user} items={userOptions} />
      </div>
    </div>
  );
};

export default MainNavigation;
